import type { AdminSubscriptionRow } from '@/api/generated';

export type DerivedStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'paused'
  | 'comped'
  | 'canceled'
  | 'creating';

export type StatusFilter = 'all' | 'active' | 'trialing' | 'past_due' | 'comped' | 'canceled';

export const STATUS_FILTERS: readonly StatusFilter[] = [
  'all',
  'active',
  'trialing',
  'past_due',
  'comped',
  'canceled',
];

function civilOf(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isComped(row: AdminSubscriptionRow, now: Date): boolean {
  if (row.origin === 'COMP') return true;
  if (!row.compedUntil) return false;
  const until = Date.parse(row.compedUntil);
  return !Number.isNaN(until) && until > now.getTime();
}

function isTrialing(row: AdminSubscriptionRow, now: Date): boolean {
  if (row.status !== 'active') return false;
  if (!row.trialEndsOn) return false;
  return row.trialEndsOn.slice(0, 10) > civilOf(now);
}

/**
 * Canvas filters derived from wire `status` + origin / trial / comp fields.
 * `creating` is only the wire's own `creating`. An absent or unknown status is `null` ("—"),
 * never an invented value.
 */
export function deriveStatus(row: AdminSubscriptionRow, now: Date): DerivedStatus | null {
  const wire = row.status;
  if (wire == null || wire === '') return null;
  if (wire === 'canceled') return 'canceled';
  if (isComped(row, now)) return 'comped';
  if (isTrialing(row, now)) return 'trialing';
  if (wire === 'active' || wire === 'past_due' || wire === 'paused' || wire === 'creating') {
    return wire;
  }
  return null;
}

export function matchesFilter(derived: DerivedStatus | null, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  return derived === filter;
}

/** Server `status` query — only the four literal BillingSubscriptionStatus values. */
export function filterToQuery(filter: StatusFilter): { status?: string } {
  if (filter === 'active' || filter === 'past_due' || filter === 'canceled') {
    return { status: filter };
  }
  return {};
}

export function isLiveDerived(derived: DerivedStatus | null): boolean {
  return derived === 'active' || derived === 'trialing' || derived === 'comped';
}

export function countLiveRows(rows: AdminSubscriptionRow[], now: Date): number {
  return rows.filter((row) => isLiveDerived(deriveStatus(row, now))).length;
}
